import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { downloadGeometryFile, GeometryFileParams, uploadGeometryFile } from "../thunks/geometryFileThunks";

export const pendingGeometriesSliceName = "pendingGeometries";

export interface PendingGeometriesState {
  statusByTask: Record<string, Record<string, Record<string, GeometryFileStatus>>>;
  downloadingGeometryIds: Record<string, number>;
  uploadingGeometryIds: Record<string, number>;
}

export type PendingType = "UPLOAD" | "DOWNLOAD";
export type GeometryFileStatus =
  | { status: "ERROR"; type: PendingType; error: unknown }
  | { status: "PENDING"; type: PendingType }
  | { status: "SUCCESS"; type: PendingType }
  ;

const initialState: PendingGeometriesState = {
  downloadingGeometryIds: {},
  uploadingGeometryIds: {},
  statusByTask: {},
};

const updateNumberOfPendingRequests = (state: Record<string, number>, geometryId: string, offset: number) => {
  if (geometryId in state && state[geometryId] + offset > 0) state[geometryId] += offset;
  else if (!(geometryId in state) && offset > 0) state[geometryId] = offset;
  // delete the key to exclude it from Object.keys() and Object.entries()
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  else delete state[geometryId];
};

type GeometryFileIdentifier = Omit<GeometryFileParams, "url">;
const getPlaceholderIdentifier = (action: PayloadAction<string>): GeometryFileIdentifier => {
  return ({ taskId: action.payload, geometryId: "PLACEHOLDER", fileName: "PLACEHOLDER" });
};

const updateStatusByTask = (state: PendingGeometriesState["statusByTask"], arg: GeometryFileIdentifier, geometryStatus: GeometryFileStatus) => {
  if (arg.taskId != null) {
    const statusByGeometry = state[arg.taskId] = state[arg.taskId] ?? {};
    const statusByFile = statusByGeometry[arg.geometryId] = statusByGeometry[arg.geometryId] ?? {};
    statusByFile[arg.fileName] = geometryStatus;
  }
};

const removeStatusByTask = (state: PendingGeometriesState["statusByTask"], arg: GeometryFileIdentifier) => {
  if (arg.taskId != null) {
    const statusByGeometry = state[arg.taskId] = state[arg.taskId] ?? {};
    const statusByFile = statusByGeometry[arg.geometryId] = statusByGeometry[arg.geometryId] ?? {};
    // delete the key to exclude it from Object.keys() and Object.entries()
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete statusByFile[arg.fileName];
  }
};

const pendingGeometriesSlice = createSlice({
  name: pendingGeometriesSliceName,
  initialState,
  reducers: {
    clearStatusByTask(state: PendingGeometriesState, action: PayloadAction<string>) {
      // delete the key to exclude it from Object.keys() and Object.entries()
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.statusByTask[action.payload];
    },
    // methods for mocking a placeholder up/download for long-running tasks with potential pauses during downloads
    addTaskPlaceholder(state: PendingGeometriesState, action: PayloadAction<string>) {
      updateStatusByTask(state.statusByTask, getPlaceholderIdentifier(action), {
        type: "PLACEHOLDER" as PendingType,
        status: "PENDING",
      });
    },
    finishTask(state: PendingGeometriesState, action: PayloadAction<string>) {
      removeStatusByTask(state.statusByTask, getPlaceholderIdentifier(action));
    },
  },
  extraReducers: (builder) => {
    builder.addCase(downloadGeometryFile.pending, (state, action) => {
      updateStatusByTask(state.statusByTask, action.meta.arg, { type: "DOWNLOAD", status: "PENDING" });
      updateNumberOfPendingRequests(state.downloadingGeometryIds, action.meta.arg.geometryId, +1);
    });
    builder.addCase(downloadGeometryFile.fulfilled, (state, action) => {
      updateStatusByTask(state.statusByTask, action.meta.arg, { type: "DOWNLOAD", status: "SUCCESS" });
      updateNumberOfPendingRequests(state.downloadingGeometryIds, action.meta.arg.geometryId, -1);
    });
    builder.addCase(downloadGeometryFile.rejected, (state, action) => {
      updateStatusByTask(state.statusByTask, action.meta.arg, { type: "DOWNLOAD", status: "ERROR", error: action.error });
      updateNumberOfPendingRequests(state.downloadingGeometryIds, action.meta.arg.geometryId, -1);
    });
    builder.addCase(uploadGeometryFile.pending, (state, action) => {
      updateStatusByTask(state.statusByTask, action.meta.arg, { type: "UPLOAD", status: "PENDING" });
      updateNumberOfPendingRequests(state.uploadingGeometryIds, action.meta.arg.geometryId, +1);
    });
    builder.addCase(uploadGeometryFile.fulfilled, (state, action) => {
      updateStatusByTask(state.statusByTask, action.meta.arg, { type: "UPLOAD", status: "SUCCESS" });
      updateNumberOfPendingRequests(state.uploadingGeometryIds, action.meta.arg.geometryId, -1);
    });
    builder.addCase(uploadGeometryFile.rejected, (state, action) => {
      updateStatusByTask(state.statusByTask, action.meta.arg, { type: "UPLOAD", status: "ERROR", error: action.error });
      updateNumberOfPendingRequests(state.uploadingGeometryIds, action.meta.arg.geometryId, -1);
    });
  },
});

export interface PendingGeometriesRootState {
  [pendingGeometriesSliceName]: PendingGeometriesState;
}

export const selectAllPendingGeometryIds = createSelector(
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].downloadingGeometryIds,
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].uploadingGeometryIds,
  (downloadingGeometryIds, uploadingGeometryIds) => {
    return [...Object.keys(downloadingGeometryIds), ...Object.keys(uploadingGeometryIds)];
  },
);

export const selectDownloadingGeometryIds = createSelector(
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].downloadingGeometryIds,
  (downloadingGeometryIds) => Object.keys(downloadingGeometryIds),
);

export const selectUploadingGeometryIds = createSelector(
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].uploadingGeometryIds,
  (uploadingGeometryIds) => Object.keys(uploadingGeometryIds),
);

export const selectDownloadCount = createSelector(
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].downloadingGeometryIds,
  (downloadingGeometryIds) => Object.values(downloadingGeometryIds).reduce((acc, value) => acc + value, 0),
);

export const selectFinishedTasks = createSelector(
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].statusByTask,
  (statusByTask) => Object.fromEntries(Object.entries(statusByTask).filter(([, value]) => (
    Object.values(value).every((geometry) => Object.values(geometry).every((file) => file.status !== "PENDING"))
  ))),
);

export const { clearStatusByTask, addTaskPlaceholder, finishTask } = pendingGeometriesSlice.actions;
export const pendingGeometriesReducer = pendingGeometriesSlice.reducer;
